// src/pages/GptChatPage.js
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { getMyGpts, sendMessageToGpt, getOrCreateCurrentChat, getBrainCompletionStatus } from "../services/api";
import { isAuthenticated, getUserFromToken } from "../utils/auth";
import styles from "../css/shared.module.css";
import TextareaAutosize from "react-textarea-autosize";
import { FiSend, FiMessageSquare, FiChevronDown } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import BrainIncompleteModal from "../components/BrainIncompleteModal";

const GptChatPage = () => {
  const { id } = useParams();
  
  // Debug: Verificar que el ID se está extrayendo correctamente
  console.log("GptChatPage - ID extraído de useParams:", id);
  
  const [gpt, setGpt] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentChatId, setCurrentChatId] = useState(null);
  // Modal de nuevo chat eliminado en chat único
  const [includeBrainContext, setIncludeBrainContext] = useState(false);
  // Modal de Brain incompleto
  const [showBrainIncompleteModal, setShowBrainIncompleteModal] = useState(false);
  const [missingEntries, setMissingEntries] = useState([]);

  const chatContainerRef = useRef(null);

  // Render helper: oculta el contexto del Brain en mensajes de usuario
  const getDisplayContent = (msg) => {
    if (msg?.role !== "user" || typeof msg?.content !== "string") return msg?.content || "";
    // Busca el marcador usado en backend
    const marker = "Mensaje del usuario:";
    const idx = msg.content.indexOf(marker);
    if (idx !== -1) {
      return msg.content.substring(idx + marker.length).trim();
    }
    return msg.content;
  };

  useEffect(() => {
    const fetchGpt = async () => {
      try {
        const response = await getMyGpts();
        if (response.success) {
          const found = response.data.find((g) => g._id === id);
          if (!found) {
            setError("GPT no encontrado o no asignado.");
          } else {
            setGpt(found);
            // Inyectar variables de tema por GPT
            if (found.palette) {
              const r = document.documentElement;
              r.style.setProperty('--gpt-primary', found.palette.primary || '#2563eb');
              r.style.setProperty('--gpt-secondary', found.palette.secondary || '#1e40af');
              r.style.setProperty('--gpt-bg', found.palette.background || '#f8fafc');
              r.style.setProperty('--gpt-bubble-user', found.palette.bubbleUser || '#2563eb');
              r.style.setProperty('--gpt-bubble-assistant', found.palette.bubbleAssistant || '#ffffff');
              r.style.setProperty('--gpt-border', found.palette.border || '#e2e8f0');
              r.style.setProperty('--gpt-accent', found.palette.accent || '#60a5fa');
            }
          }
        } else {
          setError("Error al cargar la información del GPT.");
        }
      } catch (err) {
        setError("Error al cargar la información del GPT.");
        console.error("Error fetching GPT:", err);
      }
    };
    fetchGpt();
  }, [id]);

  // Scroll automático inteligente - solo para casos específicos
  useEffect(() => {
    if (chatContainerRef.current && messages.length > 0) {
      const chatContainer = chatContainerRef.current;
      const isScrolledToBottom = chatContainer.scrollTop + chatContainer.clientHeight >= chatContainer.scrollHeight - 20;
      
      // Solo hacer scroll automático si el usuario está muy cerca del final
      // Esto evita interferir con el scroll manual del usuario
      if (isScrolledToBottom) {
        const scrollHeight = chatContainer.scrollHeight;
        const clientHeight = chatContainer.clientHeight;
        
        chatContainer.scrollTo({
          top: scrollHeight - clientHeight,
          behavior: "smooth",
        });
      }
    }
  }, [messages, loading]);

  // Función para hacer scroll al final manualmente
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      const chatContainer = chatContainerRef.current;
      const scrollHeight = chatContainer.scrollHeight;
      const clientHeight = chatContainer.clientHeight;
      
      // Hacer scroll al final con animación suave
      chatContainer.scrollTo({
        top: scrollHeight - clientHeight,
        behavior: "smooth",
      });
      
      console.log("GptChatPage - Scroll automático ejecutado:", {
        scrollHeight,
        clientHeight,
        targetScrollTop: scrollHeight - clientHeight
      });
    }
  };

  // Función para verificar si el Brain está completo
  const checkBrainComplete = async () => {
    // Solo verificar si el GPT usa Brain context
    if (!gpt?.brainContextChat && !gpt?.brainContextMessage) {
      return true; // No necesita Brain
    }

    try {
      const status = await getBrainCompletionStatus();
      if (!status.success || !status.data?.complete) {
        // Guardar las entradas faltantes para mostrarlas en el modal
        setMissingEntries(status.data?.missing || []);
        setShowBrainIncompleteModal(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error checking brain completion:", err);
      return false;
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    
    // Verificar Brain completo antes de enviar
    const brainComplete = await checkBrainComplete();
    if (!brainComplete) {
      return; // No enviar mensaje si Brain está incompleto
    }
    
    const userMessage = { role: "user", content: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);
    setError("");

    // Hacer scroll automático al final cuando se envía el mensaje del usuario
    setTimeout(() => {
      scrollToBottom();
    }, 100);

    try {
      // Si no hay chat activo, crear uno nuevo
      if (!currentChatId) {
        setError("No hay conversación activa. Inicia una nueva conversación.");
        setLoading(false);
        return;
      }

      // Enviar mensaje al GPT y obtener respuesta
      console.log("GptChatPage - Enviando mensaje al GPT:", { id, currentChatId, input, includeBrainContext });
      const response = await sendMessageToGpt(id, currentChatId, input, includeBrainContext);
      
      if (!response.success) {
        throw new Error(response.message);
      }

      // Agregar respuesta del asistente a los mensajes
      const assistantReply = {
        role: "assistant",
        content: response.data.assistantMessage.content,
        timestamp: response.data.assistantMessage.timestamp
      };

      setMessages(prev => [...prev, assistantReply]);
      
      // Hacer scroll automático al final cuando llega la respuesta del asistente
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      
      // Limpiar error si todo salió bien
      setError("");
      
    } catch (err) {
      console.error("Error sending message:", err);
      setError(err.message || "Ocurrió un error al enviar el mensaje. Por favor, intenta de nuevo.");
      
      // Remover el mensaje del usuario si falló
      setMessages(prev => prev.filter(msg => msg !== userMessage));
    } finally {
      setLoading(false);
    }
  };

  // ✅ Lógica de teclado mejorada para el textarea
  const handleKeyDown = (e) => {
    // Si se presiona Enter Y NO se está presionando Shift, envía el mensaje
    if (e.key === "Enter" && !e.shiftKey && !loading) {
      e.preventDefault(); // Previene que se cree una nueva línea
      sendMessage();
    }
  };

  // Verificar autenticación al cargar el componente
  useEffect(() => {
    if (!isAuthenticated()) {
      setError("No estás autenticado. Por favor, inicia sesión.");
      return;
    }
    
    const user = getUserFromToken();
    console.log("GptChatPage - Usuario autenticado:", user);
    console.log("GptChatPage - Token:", localStorage.getItem("token"));
    

  }, []);

  // Obtener o crear conversación única al cargar la página
  useEffect(() => {
    const initCurrentChat = async () => {
      try {
        if (!id) return;
        setLoading(true);
        setError("");
        const res = await getOrCreateCurrentChat(id);
        if (res.success) {
          setCurrentChatId(res.data.conversationId);
          setMessages(res.data.messages || []);
          setTimeout(scrollToBottom, 150);
        } else {
          setError(res.message);
        }
      } catch (e) {
        console.error("initCurrentChat error", e);
        setError("No se pudo obtener la conversación");
      } finally {
        setLoading(false);
      }
    };
    initCurrentChat();
  }, [id]);

  // Handlers de lista/nuevo chat eliminados para chat único

  if (!gpt && !error) {
    return (
      <div>
        <h1 className={styles.title}>Cargando Asistente...</h1>
        <p className={styles.subtitle}>
          Preparando la interfaz de chat, por favor espera.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.chatPageHeader}>
        <div className={styles.chatPageTitle}>
          <h1 className={styles.title}>{gpt?.name || "Chat"}</h1>
          <p className={styles.subtitle}>
            {gpt?.description || "Interactúa con tu asistente de IA."}
          </p>
        </div>
        
        {/* Acciones de cabecera removidas para chat único */}
      </div>

      <div className={`${styles.chatPageLayout} ${styles.singleColumn}`}>
        {/* Panel izquierdo eliminado para chat único */}
        <div style={{ display: 'none' }} />

        {/* Panel derecho: Chat activo o estado inicial */}
        <div className={styles.chatMain}>
          {currentChatId ? (
            <>
              {/* Chat activo */}
              <div className={styles.chatContainer} ref={chatContainerRef}>
                {messages.length === 0 ? (
                  <div className={styles.emptyChatState}>
                    <div className={styles.emptyChatIcon}>
                      <FiMessageSquare size={64} />
                    </div>
                    <h3>Conversación iniciada</h3>
                    <p className={styles.emptyChatSubtext}>
                      Escribe tu primer mensaje para comenzar a interactuar con el asistente
                    </p>
                    <div className={styles.emptyChatDecoration}>
                      <div className={styles.decorationDot}></div>
                      <div className={styles.decorationDot}></div>
                      <div className={styles.decorationDot}></div>
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, i) => (
                      <div
                        key={i}
                        className={`${styles.messageBubble} ${
                          msg.role === "user" ? styles.user : styles.assistant
                        }`}
                      >
                        <span className={styles.messageAuthor}>
                          {msg.role === "user" ? "Tú" : gpt?.name || "Asistente"}
                        </span>
                        {msg.role === "assistant" ? (
                          <div className={styles.markdownContent}>
                            <ReactMarkdown>
                              {msg.content || ""}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          getDisplayContent(msg)
                        )}
                      </div>
                    ))}
                    {loading && (
                      <div className={`${styles.messageBubble} ${styles.assistant}`}>
                        <div className={styles.typingIndicator}>
                          <span>Asistente está escribiendo</span>
                          <div className={styles.typingDots}>
                            <div className={styles.typingDot}></div>
                            <div className={styles.typingDot}></div>
                            <div className={styles.typingDot}></div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Botón de scroll al final */}
                <button
                  onClick={scrollToBottom}
                  className={styles.scrollToBottomButton}
                  title="Ir al final del chat"
                >
                  <FiChevronDown size={20} />
                </button>
              </div>

              {/* Input de chat cuando hay chat activo */}
              <div className={styles.chatInputContainer}>
                {error && (
                  <div className={styles.errorMessage}>
                    {error}
                  </div>
                )}
                
                {/* Checkbox para incluir contexto del Brain (solo si el GPT lo permite) */}
                {gpt?.brainContextMessage && (
                  <div className={styles.brainContextToggle}>
                    <input
                      type="checkbox"
                      id="includeBrainContext"
                      checked={includeBrainContext}
                      onChange={(e) => setIncludeBrainContext(e.target.checked)}
                      className={styles.checkbox}
                    />
                    <label htmlFor="includeBrainContext" className={styles.checkboxLabel}>
                      Incluir Contexto de Mi cerebro
                    </label>
                  </div>
                )}
                
                <TextareaAutosize
                  value={input}
                  placeholder="Escribe tu mensaje aquí... (Shift+Enter para nueva línea)"
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className={`${styles.input} ${styles.chatTextarea}`}
                  disabled={loading}
                  minRows={1}
                  maxRows={6}
                />
                <button
                  onClick={() => {
                    console.log("GptChatPage - Botón enviar presionado");
                    console.log("GptChatPage - Estado actual:", { currentChatId, input: input.trim(), includeBrainContext });
                    sendMessage();
                  }}
                  disabled={loading || !input.trim()}
                  className={`${styles.button} ${styles.sendButton}`}
                >
                  <FiSend size={18} />
                  <span>{loading ? "Enviando..." : "Enviar"}</span>
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Modal de Brain incompleto */}
      <BrainIncompleteModal
        isOpen={showBrainIncompleteModal}
        onClose={() => setShowBrainIncompleteModal(false)}
        gptName={gpt?.name || "Asistente"}
        missingEntries={missingEntries}
      />
    </div>
  );
};

export default GptChatPage;
